// useRetailRoleForCustomer : pour un customer donné, retourne le rôle effectif
// du user loggé vis-à-vis de ce client précis.
//
// Pourquoi un hook spécifique : un manager peut être N1 pour le client A
// et N2 pour le client B (et rien pour C). La distinction se fait via
// supervisedN1 / supervisedN2 (listes de customer_ids dénormalisées sur
// les prefs du manager, maintenues par admin_api via :
//   - POST /users, PATCH /users/:id, DELETE /users/:id (live)
//   - phase sync_supervised du job recompute (idempotent)
//
// Précédence : retail_admin = global (au-dessus de tout). Sinon on regarde
// les listes supervised. N1 prime sur N2 si présent dans les deux (cas
// rare mais possible).

import { useMemo } from 'react'
import usePrefStore from '../stores/prefs'
import type { RetailRole } from '../types'

export interface RetailRoleForCustomer {
   isAdmin: boolean       // retail_admin (global, indépendant du customer)
   isN1: boolean          // ce manager est N1 du customer donné
   isN2: boolean          // ce manager est N2 du customer donné
   isManager: boolean     // ce user est un manager (retail_manager=true)
   effectiveRole: 'admin' | 'N1' | 'N2' | null
}

// Version "pure function" utilisable en dehors d'un composant React
// (useCallback, getState, helpers). Le hook ci-dessous est un thin wrapper.
export function computeRetailRoleForCustomer(
   customerId: string | null | undefined,
   retailRole: RetailRole,
   isRetailManager: boolean,
   supervisedN1: string[],
   supervisedN2: string[],
): RetailRoleForCustomer {
   const isAdmin = retailRole === 'admin'
   const isN1 = !!customerId && supervisedN1.includes(customerId)
   const isN2 = !!customerId && supervisedN2.includes(customerId)
   const isManager = isRetailManager
   let effectiveRole: 'admin' | 'N1' | 'N2' | null = null
   if (isAdmin) effectiveRole = 'admin'
   else if (isN1) effectiveRole = 'N1'
   else if (isN2) effectiveRole = 'N2'
   return { isAdmin, isN1, isN2, isManager, effectiveRole }
}

export default function useRetailRoleForCustomer(customerId: string | null | undefined): RetailRoleForCustomer {
   const { retailRole, isRetailManager, supervisedN1, supervisedN2 } = usePrefStore()
   return useMemo(
      () => computeRetailRoleForCustomer(customerId, retailRole, isRetailManager, supervisedN1, supervisedN2),
      [customerId, retailRole, isRetailManager, supervisedN1, supervisedN2],
   )
}
